import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import GavelIcon from "@mui/icons-material/Gavel";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const TermsPage = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
            textDecoration: "none",
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary">Terms of Service</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <GavelIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Terms of Service
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Last Updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </Typography>
      </Box>

      {/* Important Notice */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Important:</strong> Please read these Terms of Service carefully
        before using NephroTrax. By accessing or using our platform, you
        agree to be bound by these terms.
      </Alert>

      {/* Content */}
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Introduction */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          1. Acceptance of Terms
        </Typography>
        <Typography variant="body1" paragraph>
          Welcome to NephroTrax. These Terms of Service ("Terms") govern
          your access to and use of the NephroTrax platform, including our
          website, mobile applications, and related services (collectively, the
          "Service").
        </Typography>
        <Typography variant="body1" paragraph>
          By creating an account, accessing, or using our Service, you agree to
          be bound by these Terms and our Privacy Policy. If you do not agree to
          these Terms, you may not use our Service.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 2. Eligibility */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          2. Eligibility
        </Typography>
        <Typography variant="body1" paragraph>
          You must meet the following requirements to use NephroTrax:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Be at least 18 years of age" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Have the legal capacity to enter into a binding agreement" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Not be prohibited from using our Service under applicable laws" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Provide accurate and complete registration information" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 3. Medical Disclaimer */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          3. Medical Disclaimer
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>IMPORTANT MEDICAL DISCLAIMER</strong>
        </Alert>
        <Typography variant="body1" paragraph>
          NephroTrax is an informational platform designed to help connect
          patients with clinical trials. Our Service:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Is NOT Medical Advice"
              secondary="Information provided on our platform does not constitute medical advice, diagnosis, or treatment recommendations"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Does NOT Replace Healthcare Providers"
              secondary="Always consult with qualified healthcare professionals regarding your medical condition and treatment options"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Does NOT Guarantee Clinical Trial Acceptance"
              secondary="Matching results are preliminary; final eligibility is determined by trial coordinators"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Is NOT Emergency Services"
              secondary="If you have a medical emergency, call 911 or visit your nearest emergency room immediately"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 4. User Accounts */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          4. User Accounts and Security
        </Typography>
        <Typography variant="body1" paragraph>
          When you create an account with NephroTrax:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Account Responsibility"
              secondary="You are responsible for maintaining the confidentiality of your account credentials and all activities under your account"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Accurate Information"
              secondary="You must provide accurate, current, and complete information and update it as necessary"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="One Account Per Person"
              secondary="You may only create one account and may not share your account with others"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Unauthorized Access"
              secondary="Notify us immediately if you suspect unauthorized access to your account"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Acceptable Use */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          5. Acceptable Use
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to use NephroTrax only for lawful purposes. You must
          NOT:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Violate any applicable laws or regulations" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Infringe upon intellectual property rights of others" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Upload or transmit viruses, malware, or harmful code" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Attempt to gain unauthorized access to our systems" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Harass, abuse, or harm other users" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Submit false or misleading information" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Use automated systems (bots, scrapers) without permission" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Reverse engineer or decompile our software" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 7. Intellectual Property */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          6. Intellectual Property Rights
        </Typography>
        <Typography variant="body1" paragraph>
          The Service and its original content, features, and functionality are
          owned by NephroTrax and are protected by international copyright,
          trademark, patent, trade secret, and other intellectual property laws.
        </Typography>
        <Typography variant="body1" paragraph>
          You retain ownership of content you submit to our platform. By
          submitting content, you grant us a worldwide, non-exclusive,
          royalty-free license to use, reproduce, and display your content
          solely to provide the Service.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 8. Third-Party Links */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          7. Third-Party Services and Links
        </Typography>
        <Typography variant="body1" paragraph>
          Our Service may contain links to third-party websites, including
          ClinicalTrials.gov and trial sponsor sites. We do not endorse or
          assume responsibility for:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Content, accuracy, or availability of third-party sites" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Privacy practices of external websites" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Clinical trials operated by third parties" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 8. Limitation of Liability */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEPHROTRAX SHALL NOT BE
          LIABLE FOR:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Indirect, incidental, special, consequential, or punitive damages" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Loss of profits, data, use, or goodwill" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Service interruptions or technical errors" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Medical decisions made based on our platform" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Actions or omissions of clinical trial sponsors" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 9. Indemnification */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          9. Indemnification
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to indemnify, defend, and hold harmless NephroTrax and
          its officers, directors, employees, and agents from any claims,
          liabilities, damages, losses, and expenses arising out of:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Your use of the Service" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Your violation of these Terms" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Your violation of any third-party rights" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 10. Termination */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          10. Termination
        </Typography>
        <Typography variant="body1" paragraph>
          We may terminate or suspend your account and access to the Service
          immediately, without prior notice, for:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Violation of these Terms" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Fraudulent, abusive, or illegal activity" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Request by law enforcement" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Extended periods of inactivity" />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Upon termination, your right to use the Service will immediately
          cease. We may retain certain information as required by law or for
          legitimate business purposes.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 12. Changes to Terms */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          11. Changes to Terms of Service
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right to modify these Terms at any time. If we make
          material changes, we will:
        </Typography>
        <List>
          <ListItem>
            <ListItemText secondary="Update the 'Last Updated' date" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Notify you via email or platform notification" />
          </ListItem>
          <ListItem>
            <ListItemText secondary="Provide at least 30 days notice for material changes" />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Your continued use of the Service after changes constitutes acceptance
          of the new Terms.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 13. Governing Law */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          12. Governing Law and Dispute Resolution
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms shall be governed by and construed in accordance with the
          laws of the State of California, United States, without regard to its
          conflict of law provisions.
        </Typography>
        <Typography variant="body1" paragraph>
          Any disputes arising from these Terms or the Service shall be resolved
          through binding arbitration in San Francisco, California, except that
          either party may seek injunctive relief in court.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 13. Contact Information */}
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          13. Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
          For questions about these Terms of Service, please contact us:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Email"
              secondary="legal@nephrotrax.com"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Mail"
              secondary={
                <>
                  NephroTrax Legal Department
                  <br />
                  123 Medical Plaza, Suite 500
                  <br />
                  San Francisco, CA 94102
                </>
              }
            />
          </ListItem>
        </List>
      </Paper>

      {/* Related Links */}
      <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{ textDecoration: "none" }}
        >
          Privacy Policy
        </Link>
        <Typography color="text.secondary">•</Typography>
        <Link
          component={RouterLink}
          to="/hipaa"
          sx={{ textDecoration: "none" }}
        >
          HIPAA Compliance
        </Link>
        <Typography color="text.secondary">•</Typography>
        <Link
          component={RouterLink}
          to="/contact"
          sx={{ textDecoration: "none" }}
        >
          Contact Us
        </Link>
      </Box>
    </Container>
  );
};

export default TermsPage;